import type { PostHog } from "posthog-js"

// posthog-js loads after hydration (see usePostHogInit), so app code reaches
// it through here instead of importing it.
let client: PostHog | undefined
let status: "loading" | "ready" | "off" = "loading"
let queue: ((posthog: PostHog) => void)[] | undefined = []
const listeners = new Set<() => void>()

/** The PostHog client once it has initialised. */
export const getPostHog = () => client

/** "off" while PostHog will not start, so flag readers can stop waiting. */
export const getPostHogStatus = () => status

/**
 * Calls `listener` when the client initialises or PostHog turns off. Unlike
 * `withPostHog`, it still works after `disablePostHogQueue`, e.g. when a
 * visitor accepts after rejecting.
 */
export function subscribePostHog(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Runs `fn` now if PostHog is ready, else once it initialises. */
export function withPostHog(fn: (posthog: PostHog) => void) {
  if (client) fn(client)
  else queue?.push(fn)
}

/** Called after init: replays the calls made before it, in order. */
export function setPostHogClient(posthog: PostHog) {
  client = posthog
  status = "ready"
  const pending = queue ?? []
  queue = undefined
  for (const fn of pending) fn(posthog)
  for (const listener of listeners) listener()
}

/** PostHog will not start (dev server, no key, consent rejected, chunk failed). */
export function disablePostHogQueue() {
  queue = undefined
  if (status !== "loading") return
  status = "off"
  for (const listener of listeners) listener()
}
